<template>
  <div class="dialogue-container">
    <header class="dialogue-header">
      <div class="left-action" @click="goBack">
        <i class="el-icon-arrow-left" />
      </div>
      <span class="chat-title">{{ chatReceiver.screenName }}</span>
      <i class="el-icon-more right-icon" @click="handleMore" />
    </header>

    <div ref="messageBox" class="message-container" @scroll="handleScroll">

      <div v-if="hasOlder && isLoading && isPageLoadingOlder" class="loading-tips">
        <i class="el-icon-loading" /> 正在加载历史消息...
      </div>

      <div
        v-for="(msg, index) in formattedMessageList"
        :key="msg.messageId + index"
      >
        <div v-if="msg._showTimeText" class="chat-time-node">
          <span>{{ msg._showTimeText }}</span>
        </div>

        <div
          :id="'msg-' + msg.messageId"
          :class="['message-row', isMe(msg.sender.userId) ? 'msg-me' : 'msg-other']"
        >
          <el-avatar
            :size="38"
            shape="square"
            :src="isMe(msg.sender.userId) ? my.avatarUrl : (msg.sender.avatarUrl || chatReceiver.avatarUrl)"
          />

          <div class="message-content">
            <div v-if="msg.msgType === 1" class="msg-text">{{ msg.content }}</div>

            <div v-else-if="msg.msgType === 22" class="msg-image" @click="onPreview(msg.objectUrl)">
              <el-image :src="msg.objectUrl" fit="cover" />
            </div>

            <div
              v-else-if="msg.msgType === 23"
              class="msg-audio"
              :style="{ width: 60 + (msg.duration || 5) * 4 + 'px' }"
              @click="toggleAudio(msg, msg.messageId)"
            >
              <i :class="['audio-icon', currentPlayingId === msg.messageId ? 'el-icon-loading' : 'el-icon-phone-outline']" />
              <span class="audio-duration">{{ msg.duration || 5 }}"</span>
              <div v-if="msg.isUnread" class="audio-unread-dot" />
            </div>

            <div v-else-if="msg.msgType === 24" class="msg-video" @click="viewVideo(msg.objectUrl)">
              <el-image :src="require('@/assets/img/video.jpg')" fit="cover" class="video-cover" />
              <div class="video-play-mask">
                <i class="el-icon-video-play play-icon" />
              </div>
            </div>

            <div
              v-else-if="[21, 25, 26, 27].includes(msg.msgType)"
              :class="['msg-file', { 'is-expired': msg.fileExpired }]"
              @click="msg.fileExpired ? handleExpiredClick() : handleFileClick(msg)"
            >
              <div class="file-info">
                <div class="file-name">{{ msg.fileName || '未知文件' }}</div>

                <div v-if="msg.fileExpired" class="file-expired-tips">
                  <i class="el-icon-warning" /> 文件已过期
                </div>
                <div v-else class="file-size">
                  {{ msg.fileSize || '0 KB' }}
                </div>
              </div>

              <div class="file-icon-box">
                <i :class="getFileIcon(msg.msgType)" />
              </div>
            </div>

            <div v-else-if="msg.msgType === 5" class="msg-record">
              <div class="record-title">聊天记录</div>
              <div v-for="(line, idx) in msg.previewLines" :key="idx" class="record-preview">
                {{ line }}
              </div>
            </div>

            <div v-else-if="msg.msgType === 6">
              <div>语音通话 {{ msg.discardReason }} - {{ msg.duration }}</div>
            </div>
            <div v-else-if="msg.msgType === 7">
              <div>视频通话 {{ msg.discardReason }} - {{ msg.duration }}</div>
            </div>
            <div
              v-else-if="msg.msgType === 6"
              :class="['msg-call-bubble', isMe(msg.sender.userId) ? 'is-self' : 'is-other']"
            >
              <i v-if="!isMe(msg.sender.userId)" class="el-icon-phone-outline call-icon" />

              <div class="call-content">
                <span class="call-text">{{ getCallStatusText(msg) }}</span>
                <span v-if="msg.duration" class="call-duration">{{ msg.duration }}</span>
              </div>

              <i v-if="isMe(msg.sender.userId)" class="el-icon-phone-outline call-icon" />
            </div>

            <div
              v-else-if="msg.msgType === 7"
              :class="['msg-call-bubble', isMe(msg.sender.userId) ? 'is-self' : 'is-other']"
            >
              <i v-if="!isMe(msg.sender.userId)" class="el-icon-video-camera call-icon" />

              <div class="call-content">
                <span class="call-text">{{ getCallStatusText(msg) }}</span>
                <span v-if="msg.duration" class="call-duration">{{ msg.duration }}</span>
              </div>

              <i v-if="isMe(msg.sender.userId)" class="el-icon-video-camera call-icon" />
            </div>

            <div
              v-else-if="msg.msgType === 2"
              :class="['msg-transfer', { 'is-received': msg.transferStatus === 2 }]"
              @click="handleTransferClick(msg)"
            >
              <div class="transfer-main">
                <div class="transfer-icon-box">
                  <i :class="msg.transferStatus === 2 ? 'el-icon-check' : 'el-icon-refresh'" />
                </div>
                <div class="transfer-info">
                  <div class="transfer-amount">¥{{ msg.amount || '0.00' }}</div>
                  <div class="transfer-status">
                    <span v-if="msg.transferStatus === 1">待接收 / 点击收钱</span>
                    <span v-else-if="msg.transferStatus === 2">已被接收</span>
                  </div>
                </div>
              </div>
              <div class="transfer-footer">微信转账</div>
            </div>

            <div
              v-else-if="msg.msgType === 3"
              :class="['msg-lucky-money', { 'is-opened': msg.isSelfOpened }]"
              @click="handleLuckyMoneyClick(msg)"
            >
              <div class="hb-main">
                <div class="hb-icon-box">
                  <i :class="msg.isSelfOpened ? 'el-icon-folder-opened' : 'el-icon-wallet'" />
                </div>
                <div class="hb-info">
                  <div class="hb-title">{{ msg.content || '恭喜发财，大吉大利' }}</div>

                  <div class="hb-status">
                    <span v-if="msg.isSelfOpened">
                      {{ msg.chatType === 2 ? '已抢到红包' : '红包已被接收' }}
                    </span>
                    <span v-else-if="msg.chatType === 2 && msg.transferStatus === 2">
                      红包已被领完
                    </span>
                    <span v-else-if="msg.transferStatus === 3">
                      红包已过期
                    </span>
                  </div>
                </div>
              </div>
              <div class="hb-footer">
                {{ msg.chatType === 2 ? '微信群红包' : '微信红包' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasNewer && isLoading && isPageLoadingNewer" class="loading-tips">
        <i class="el-icon-loading" /> 正在加载新消息...
      </div>
    </div>

    <footer class="input-container">
      <div class="tool-bar">
        <i class="el-icon-microphone" @click="handleMore" />
        <el-input v-model="inputText" type="text" size="small" placeholder="发送消息..." @keyup.enter.native="sendMessage" />
        <i class="el-icon-aim" @click="handleMore" />
        <el-button type="success" size="mini" class="send-btn" @click="sendMessage">发送</el-button>
      </div>
    </footer>

    <audio
      v-if="currentAudioUrl"
      ref="audioPlayer"
      :src="currentAudioUrl"
      style="display: none;"
      @ended="onAudioEnded"
      @error="onAudioError"
    />

    <el-dialog
      :visible.sync="videoDialogVisible"
      width="90%"
      max-width="400px"
      custom-class="video-player-dialog"
      :append-to-body="true"
      @close="closeVideoPlayer"
    >
      <div class="dialog-video-wrapper">
        <video
          v-if="videoDialogVisible"
          ref="previewVideo"
          :src="currentVideoUrl"
          controls
          autoplay
          class="preview-video-element"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getChatDialogue, getChatRecords, updateChatDialogue } from '@/api/chat'

export default {
  name: 'ChatDialogue',
  data() {
    return {
      my: null,
      inputText: '',
      currentAudioUrl: '',
      currentPlayingId: null,
      currentContact: {
        chatId: 0,
        receiverId: 0
      },
      chatReceiver: {
        userId: 0,
        screenName: '',
        avatarUrl: ''
      },
      // 2. 状态控制与渲染层解耦变量
      messageList: [], // 视图层真正循环渲染的数组
      lastReadMessageId: 0, // 后端持久化记录的用户上次查看的 Anchor ID

      // 双向分页开关
      hasOlder: false, // 初始化默认为 true，若请求触顶且后端返回数据为空或判断到头则置为 false
      hasNewer: false, // 初始化默认为 true，向下滚动加载更新的标记
      isLoading: false, // 节流阀锁
      isPageLoadingOlder: false, // 是否是“主动向上拉取历史”引发的加载
      isPageLoadingNewer: false, // 是否是“主动向下拉取新消息”引发的加载
      videoDialogVisible: false,
      currentVideoUrl: ''
    }
  },
  computed: {
    /**
     * 带有时间聚合标记的渲染消息列表
     */
    formattedMessageList() {
      // const TIME_THRESHOLD = 5 * 60 * 1000 // 时间差阈值：5分钟（毫秒）
      const TIME_THRESHOLD = 3 * 1000 // 时间差阈值：5分钟（毫秒）

      return this.messageList.map((msg, index) => {
        // 深拷贝或浅拷贝出一个用于渲染的新对象，避免直接污染原数组
        const rMsg = { ...msg }

        // 1. 第一条消息无条件显示时间
        if (index === 0) {
          rMsg._showTimeText = this.formatChatTime(rMsg.createdAt)
          return rMsg
        }

        // 2. 计算当前消息与上一条消息的时间差
        const prevMsg = this.messageList[index - 1]
        const prevTime = new Date(prevMsg.createdAt).getTime()
        const currTime = new Date(rMsg.createdAt).getTime()

        if (currTime - prevTime > TIME_THRESHOLD) {
          rMsg._showTimeText = this.formatChatTime(rMsg.createdAt)
        } else {
          rMsg._showTimeText = null
        }

        return rMsg
      })
    }
  },
  created() {
    this.my = this.$store.state.userInfo
    this.parseRouteParams()
  },
  methods: {
    handleMore() {
      this.$message.info('功能开发中')
    },
    /**
     * 点击转账气泡
     */
    handleTransferClick(msg) {
      // 如果已经收过钱了，直接返回或看详情
      if (msg.transferStatus === 2) {
        this.$message.info('该转账已完成，可在钱包明细中查看')
        return
      }

      // 触发高仿微信收钱动画
      const loading = this.$loading({
        lock: true,
        text: '正在收钱...',
        spinner: 'el-icon-loading', // 产生菊花转圈动效
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'wechat-loading-mask' // 注入自定义样式，包装成微信黑框提示
      })

      // 模拟网络请求延迟（1.5秒后收钱成功）
      setTimeout(() => {
        loading.close() // 关闭收钱动画

        // 弹出成功提示
        this.$message({
          message: `成功入账 ¥${msg.amount} 元！`,
          type: 'success'
        })

        // 核心：改变状态。由于给外层加了 transition，气泡颜色会当场丝滑变淡
        this.$set(msg, 'transferStatus', 2)

        // 实际业务：此处应该向后端发请求更新数据库：
        // receiveTransfer({ messageId: msg.messageId }).then(resp => { ... })
      }, 1500)
    },
    handleLuckyMoneyClick(msg) {
      // 1. 如果已经过期了
      if (msg.transferStatus === 3) {
        this.$message.info('该红包已超过24小时，已过期失效')
        return
      }

      // 2. 如果是群聊红包 (chatType === 2)
      if (msg.chatType === 2) {
        if (msg.isSelfOpened) {
          // 自己抢过了，点击直接看【群红包领取详情列表】（看手气最佳是谁）
          this.$message.info('正在查看群红包领取详情...')
        } else if (msg.transferStatus === 1) {
          // 自己没抢过，但红包空了，点击弹窗提示“手慢了，红包派完了”，并把本地状态改为已读变灰
          this.$message.warning('手慢了，红包已被领完！')
          this.$set(msg, 'isSelfOpened', true)
        } else {
          // 核心：有福同享，啪！弹出微信大红圈“開”字动画
          this.$message.success('弹出群红包【開】弹窗，开始拼手气！')
          // 成功抢到后后端配合把 msg.isSelfOpened 改为 true
        }
      } else {
        // 3. 如果是单聊点对点红包 (chatType === 1)
        if (msg.isSelfOpened || msg.transferStatus === 1) {
          this.$message.info('查看红包领取金额')
        } else {
          this.$message.success('成功拆开好友红包！')
          this.$set(msg, 'isSelfOpened', true)
          this.$set(msg, 'transferStatus', 2)
        }
      }
    },
    /**
     * 点击过期文件的提示
     */
    handleExpiredClick() {
      this.$message.error('该文件已超过7天有效期，无法下载或预览')
    },
    handleFileClick(msg) {
      this.$message.info('get file ' + msg.content)
      // 1. 校验下载地址是否存在
      // 提示：我们在 initFirstScreen 中清洗数据时，已经把 "文件名|大小|地址" 切割并赋值给了 msg.fileUrl
      const url = msg.fileUrl
      if (!url) {
        this.$message.warning('该文件暂不支持下载（地址失效）')
        return
      }

      // 2. 根据文件后缀或 msgType 判断策略
      // msgType 对应关系参考：25-Word, 26-Excel, 27-PDF, 99-ZIP
      if (msg.msgType === 27 || url.toLowerCase().endsWith('.pdf')) {
        // ==== 策略 A：在线预览 (主要针对 PDF 或高版本浏览器支持的公有流) ====
        // window.open(url, '_blank')
      } else {
        // ==== 策略 B：强制下载 (Word, Excel, Zip 等) ====
        // this.downloadFile(url, msg.fileName || '未知文件')
      }
    },

    /**
     * 触发浏览器原生下载二进制流/文件流
     */
    downloadFile(url, fileName) {
      this.$message.info('正在发起文件下载...')

      // 如果文件和前端同源，或后端支持 CROS 跨域，推荐使用 <a> 标签 + download 属性
      const link = document.createElement('a')
      link.href = url
      link.style.display = 'none'
      // 强制指定下载下来的文件名
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()

      // 释放节点
      document.body.removeChild(link)
    },
    /**
     * 仿微信聊天时间精细化格式化引擎
     * @param {String|Date} timeStr 后端返回的 ISO 或标准时间字符串 (例如 msg.createdAt)
     */
    formatChatTime(timeStr) {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      const now = new Date()

      const diffMs = now.getTime() - date.getTime()
      const diffDays = Math.floor(diffMs / (24 * 3600 * 1000))

      // 提取时分补零
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const timePart = `${hours}:${minutes}`

      // 1. 同一天
      if (date.toDateString() === now.toDateString()) {
        return timePart
      }

      // 2. 昨天
      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return `昨天 ${timePart}`
      }

      // 3. 同一年内但超过昨天
      if (date.getFullYear() === now.getFullYear()) {
        return `${date.getMonth() + 1}月${date.getDate()}日 ${timePart}`
      }

      // 4. 跨年
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${timePart}`
    },
    /**
     * 精准解析并转换路由传入的 Long 型参数
     */
    parseRouteParams() {
      const query = this.$route.query

      // 1. 防御性拦截：如果关键参数缺失，进行报错提示或重定向
      if (!query.chatId) {
        this.$message.error('会话参数缺失，正在返回列表...')
        setTimeout(() => {
          this.goBack()
        }, 1500)
        return
      }

      // 2. 类型转换 (将 String 转为 JavaScript 的标准 Number，对应 Java 的 Long)
      // 注意：如果你的分布式雪花 ID 超过了 16 位（大于 9007199254740991），
      // 请不要转成 Number，保持字符串进行传输，否则 JS 会丢失后三位精度！
      this.currentContact.chatId = Number(query.chatId)
      // 3. 联动逻辑：如果路由中没有传 lastMessageId，则强制补 0
      // this.lastReadMessageId = query.lastMessageId ? Number(query.lastMessageId) : 0
      // this.lastReadMessageId = 0
      const queryParams = {}
      queryParams.chatId = this.currentContact.chatId
      getChatDialogue(queryParams).then(resp => {
        if (resp.code === 0) {
          this.chatReceiver = resp.data.receiver
          this.lastReadMessageId = resp.data.lastMessageId
        }
      }).finally(() => {
        // 4. 参数解析完成后，正式向后端发起切片请求
        // 页面创建时执行核心链条：先通过锚点初始化首屏切片
        this.initFirstScreen()
      })
    },
    // 助手函数：比对 userId 判断是否是“我”发送的
    isMe(senderId) {
      return Number(senderId) === Number(this.my.userId)
    },

    /**
     * 核心生命周期方法A：首屏初始化定位
     * 围绕上次阅读位置 lastReadMessageId 去查询它前后的邻近消息
     */
    initFirstScreen() {
      this.isLoading = true

      const queryParams = {
        chatId: this.currentContact.chatId,
        messageId: this.lastReadMessageId, // 传入锚点 ID
        direction: 'init' // 自定义策略标识，告知后端需要加载此 ID 往上 5 条及往下 5 条
      }

      getChatRecords(queryParams).then(resp => {
        if (resp.code === 0) {
          resp.data.list.forEach(msg => {
            if ([25, 26, 27].includes(msg.msgType) && msg.content) {
              // 假设后端 content 规则为 "文件名|大小|下载地址"
              const parts = msg.content.split('|')
              msg.fileName = parts[0] || '未知文件.docx'
              msg.fileSize = parts[1] || '0 KB'
              msg.fileUrl = parts[2] || ''
              msg.fileExpired = true
            }
          })

          // 规范后端有可能因为无序返回，在前端按长整型 ID 重新做一次升序排列保证聊天时间线正常
          this.messageList = resp.data.list.sort((a, b) => a.messageId - b.messageId)

          // 根据后端给出的总链条状态决定是否还能继续拉取
          this.hasOlder = resp.data.hasPrev // 或者是通过后端特定规则进行首屏判定
          this.hasNewer = resp.data.hasNext

          // 精准复位将该消息钉在可视区域正中央
          // this.scrollToMessage(this.lastReadMessageId)
          this.$nextTick(() => {
            if (Number(this.lastReadMessageId) === 0) {
              this.scrollToBottom()

              if (this.messageList.length > 0) {
                this.lastReadMessageId = this.messageList[this.messageList.length - 1].messageId

                // 可选：在这里调用后端接口，异步上报已读回执/更新数据库中的 last_link_id
                // this.reportLastReadId(this.lastReadMessageId);
                const payload = {}
                payload.chatId = this.currentContact.chatId
                payload.lastMessageId = this.lastReadMessageId
                updateChatDialogue(payload).then(resp => {
                })
              }
            } else {
              // 精准定位到特定的某条历史消息
              this.scrollToMessage(this.lastReadMessageId)
            }
          })
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const box = this.$refs.messageBox
        if (box) {
          // 将滚动条拉到最底部：
          // 盒子的总高度（包含溢出隐藏的部分）直接赋值给滚动偏移量
          box.scrollTop = box.scrollHeight
        }
      })
    },
    /**
     * 核心监听：判定触顶/触底边界
     */
    handleScroll() {
      if (this.isLoading) return
      const box = this.$refs.messageBox
      if (!box) return

      const threshold = 15 // 触边缓冲像素

      // 1. 向上滚动触顶 -> 抓取当前页面最顶端一条消息的 messageId 传向后端，拉取更旧记录
      if (box.scrollTop <= threshold && this.hasOlder) {
        this.loadMoreData('older')
      }

      // 2. 向下滚动触底 -> 抓取当前页面最底端一条消息的 messageId 传向后端，拉取更新消息
      const scrollBottom = box.scrollHeight - box.clientHeight - box.scrollTop
      if (scrollBottom <= threshold && this.hasNewer) {
        this.loadMoreData('newer')
      }

      // ==== 2. 【新增】视窗滚动时，动态捕捉当前屏幕顶部的消息作为新锚点 ====
      // 采用防抖（Debounce）优化性能，避免滚动时频繁计算
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => {
        this.updateCurrentAnchor()
      }, 200)
    },
    updateCurrentAnchor() {
      if (this.messageList.length === 0) return

      const box = this.$refs.messageBox
      const containerTop = box.getBoundingClientRect().top

      // 遍历当前渲染的所有消息 DOM 节点
      for (const msg of this.messageList) {
        const el = document.getElementById(`msg-${msg.messageId}`)
        if (el) {
          const rect = el.getBoundingClientRect()
          // 当某个消息气泡的底部穿过了容器顶部，且其头部在可见区附近时
          if (rect.bottom > containerTop + 10) {
            // 这条消息就是用户当前正在看的第一条消息
            if (this.lastReadMessageId !== msg.messageId) {
              this.lastReadMessageId = msg.messageId

              const payload = {}
              payload.chatId = this.currentContact.chatId
              payload.lastMessageId = this.lastReadMessageId
              updateChatDialogue(payload).then(resp => {
              })
            }
            break
          }
        }
      }
    },
    /**
     * 核心异步翻页：双向动态加载器
     */
    loadMoreData(direction) {
      if (this.messageList.length === 0) return
      this.isLoading = true
      if (direction === 'older') {
        this.isPageLoadingOlder = true // 此时顶部的“正在加载历史消息...”才会真正显示出来！
      } else {
        this.isPageLoadingNewer = true // 此时底部的“正在加载新消息...”才会真正显示出来！
      }

      const box = this.$refs.messageBox
      const queryParams = {
        chatId: this.currentContact.chatId,
        direction: direction // 'older' 或 'newer'
      }

      if (direction === 'older') {
        // 向上：边界锚点为当前渲染队列的第一条
        queryParams.messageId = this.messageList[0].messageId
        const oldScrollHeight = box.scrollHeight

        getChatRecords(queryParams).then(resp => {
          if (resp.code === 0 && resp.data && resp.data.list && resp.data.list.length > 0) {
            const fetchedList = resp.data.list.sort((a, b) => a.messageId - b.messageId)
            // 向前合并数组
            this.messageList = [...fetchedList, ...this.messageList]

            // 体验守护：计算滚动差并悄悄复位，防画面向下闪跳
            this.$nextTick(() => {
              box.scrollTop = box.scrollHeight - oldScrollHeight
            })
          } else {
            // 后端没有返回更多历史，说明触顶封顶了
            this.hasOlder = false
          }
        }).finally(() => {
          this.isLoading = false
          this.isPageLoadingOlder = false
        })
      } else {
        // 向下：边界锚点为当前渲染队列的最后一条
        queryParams.messageId = this.messageList[this.messageList.length - 1].messageId

        getChatRecords(queryParams).then(resp => {
          if (resp.code === 0 && resp.data && resp.data.list && resp.data.list.length > 0) {
            const fetchedList = resp.data.list.sort((a, b) => a.messageId - b.messageId)
            // 向后合并数组
            this.messageList = [...this.messageList, ...fetchedList]
            this.hasNewer = resp.data.hasNext
          } else {
            this.hasNewer = false
          }
        }).finally(() => {
          this.isLoading = false
          this.isPageLoadingNewer = false
        })
      }
    },

    // 锚点精确定位 DOM 渲染高度
    scrollToMessage(msgId) {
      this.$nextTick(() => {
        const targetEl = document.getElementById(`msg-${msgId}`)
        if (targetEl) {
          targetEl.scrollIntoView({ block: 'center', behavior: 'auto' })
        }
      })
    },

    // 返回列表页
    goBack() {
      // this.messageList = []
      this.$router.push('/chat/list')
    },

    // 发送消息
    sendMessage() {
      if (!this.inputText.trim()) return

      // 真实场景下需要调用后端的 sendMessage 接口，这里先模拟单推追加视图
      const mockNewMsgId = new Date().getTime() // 零时主键
      const fakeNewMsg = {
        messageId: mockNewMsgId,
        chatId: this.currentContact.chatId,
        createdAt: new Date().toISOString(),
        msgType: 1,
        content: this.inputText,
        sender: { userId: this.my.userId },
        receiver: { userId: 0 }
      }

      this.messageList.push(fakeNewMsg)
      this.inputText = ''
      this.hasNewer = false // 用户发新消息，强制切回最新视窗状态

      this.$nextTick(() => {
        const box = this.$refs.messageBox
        box.scrollTop = box.scrollHeight // 强制置底
      })
    },
    getFileIcon(type) {
      const iconMap = {
        25: 'el-icon-document text-word',
        26: 'el-icon-s-order text-excel',
        27: 'el-icon-document-checked text-pdf',
        28: 'el-icon-box text-zip'
      }
      return iconMap[type] || 'el-icon-document'
    },
    toggleAudio(msg, id) {
      const player = this.$refs.audioPlayer
      if (!player) return
      if (msg.isUnread) msg.isUnread = false

      if (this.currentPlayingId === id) {
        player.pause()
        this.currentPlayingId = null
      } else {
        this.currentPlayingId = id
        this.currentAudioUrl = msg.objectUrl
        this.$nextTick(() => {
          player.load()
          player.play().catch(() => { this.currentPlayingId = null })
        })
      }
    },

    onAudioEnded() { this.currentPlayingId = null },
    onAudioError() { this.$message.error('语音加载失败'); this.currentPlayingId = null },

    viewVideo(url) {
      this.currentVideoUrl = url
      this.videoDialogVisible = true
    },

    closeVideoPlayer() {
      const videoElement = this.$refs.previewVideo
      if (videoElement) {
        videoElement.pause()
      }
      this.currentVideoUrl = ''
    },

    onPreview(url) {
      if (!url) return
      this.$viewerApi({
        options: {
          toolbar: true,
          navbar: false,
          title: false,
          movable: true,
          zoomable: true,
          transition: true
        },
        images: [url]
      })
    },

    /**
     * 根据拒绝原因和发送方，动态翻译成微信原生通话文案
     * msg.discardReason 可选值示例:
     * 'SUCCESS'(通话成功), 'CANCEL'(主动取消), 'REJECT'(对方拒绝), 'TIMEOUT'(无人接听)
     */
    getCallStatusText(msg) {
      const isSelf = this.isMe(msg.sender.userId) // 是否是当前登录用户发出的
      const reason = msg.discardReason

      if (reason === 'SUCCESS' || reason === 'HANGUP') {
        return '通话时长 '
      }

      if (reason === 'CANCEL') {
        return isSelf ? '已取消' : '对方已取消'
      }

      if (reason === 'REJECT') {
        return isSelf ? '对方已拒绝' : '已拒绝'
      }

      if (reason === 'TIMEOUT' || reason === 'MISSED') {
        return isSelf ? '对方无应答' : '未接听'
      }

      return '通话结束'
    }
  }
}
</script>

<style scoped>
/* 核心调整：外层容器加上 overflow: hidden; */
.dialogue-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  height: 100vh;
  margin: 0 auto;
  background-color: #f3f3f3;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  overflow: hidden; /* 关键：切断一切外层溢出，禁掉外层滚动条 */
  box-sizing: border-box;
}

/* 顶部导航 */
.dialogue-header {
  height: 50px;
  background-color: #ededed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0; /* 关键：防止高度被挤压 */
}
.left-action { cursor: pointer; display: flex; align-items: center; font-size: 16px; }
.chat-title { font-size: 16px; font-weight: bold; }
.right-icon { font-size: 18px; cursor: pointer; }

/* 消息流区域：高度自适应并允许滚动 */
.message-container {
  flex: 1;             /* 关键：自动撑满 Header 和 Footer 之间的剩余高度 */
  padding: 16px;
  overflow-y: auto;    /* 关键：只有这里允许出现垂直滚动条 */
  background-color: #f3f3f3;
}

.message-row {
  display: flex;
  margin-bottom: 18px;
}
.message-content {
  max-width: 70%;
  margin: 0 10px;
}
.msg-other { flex-direction: row; }
.msg-me { flex-direction: row-reverse; }

/* ==================== 各种消息类型的专属样式 ==================== */
.msg-text {
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 15px;
  line-height: 1.4;
  word-break: break-all;
}
.msg-other .msg-text { background-color: #fff; color: #000; }
.msg-me .msg-text { background-color: #95ec69; color: #000; }

.msg-image {
  cursor: pointer; /* 鼠标悬停时显示小手 */
}
.msg-image .el-image {
  max-width: 150px;
  max-height: 200px;
  border-radius: 4px;
  overflow: hidden;
  display: block;
}

.msg-audio {
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 60px;
  box-sizing: border-box;
}
.msg-other .msg-audio { background-color: #fff; }
.msg-me .msg-audio { background-color: #95ec69; flex-direction: row-reverse; }
.audio-icon { font-size: 16px; }
.audio-duration { margin: 0 6px; color: #666; font-size: 13px; }
.audio-unread-dot {
  position: absolute;
  top: 50%;
  right: -15px;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background-color: #fa5151;
  border-radius: 50%;
}
.msg-me .audio-unread-dot { display: none; }

.msg-video video {
  max-width: 200px;
  border-radius: 4px;
  background-color: #000;
}

.msg-file {
  background-color: #fff;
  padding: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 220px;
  border: 1px solid #e4e4e4;
  box-sizing: border-box;
}
.file-info { flex: 1; margin-right: 10px; overflow: hidden; }
.file-name { font-size: 14px; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.file-size { font-size: 12px; color: #999; }
.file-icon-box { font-size: 32px; }
.text-word { color: #2b579a; }
.text-excel { color: #217346; }
.text-pdf { color: #d9383a; }
.text-zip { color: #f39c12; }

.msg-record {
  background-color: #fff;
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid #e4e4e4;
  width: 200px;
  box-sizing: border-box;
}
.record-title { font-size: 14px; color: #333; font-weight: 500; padding-bottom: 6px; border-bottom: 1px solid #f2f2f2; margin-bottom: 6px; }
.record-preview { font-size: 12px; color: #777; margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 底部输入框 */
.input-container {
  background-color: #f7f7f7;
  border-top: 1px solid #e0e0e0;
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  flex-shrink: 0; /* 关键：防止高度被挤压 */
}
.tool-bar { display: flex; align-items: center; }
.tool-bar i { font-size: 24px; margin: 0 8px; color: #2c3e50; }
.send-btn { margin-left: 8px; }

.loading-tips {
  text-align: center;
  color: #a0a0a0;
  font-size: 13px;
  padding: 10px 0;
}

/* 4. 视频消息气泡（封面及图标控制） */
.msg-video {
  position: relative;
  max-width: 150px;
  max-height: 200px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  display: block;
}

.video-cover {
  width: 100%;
  height: 100%;
  display: block;
}

/* 覆盖在封面上层的播放按钮遮罩 */
.video-play-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.15); /* 淡淡的黑色滤镜，突出白色图标 */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.video-play-mask:hover {
  background-color: rgba(0, 0, 0, 0.3); /* 鼠标悬停加深 */
}

.play-icon {
  font-size: 40px;
  color: rgba(255, 255, 255, 0.85); /* 微信经典的半透明白 */
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* ==================== 弹窗播放器专属样式 ==================== */
/* 如果要修改 Element UI Dialog 的内部边距，可以使用深度作用选择器（如有必要） */
::v-deep .video-player-dialog {
  background: transparent !important; /* 微信风格：纯黑或全透明背景 */
  box-shadow: none !important;
}
::v-deep .video-player-dialog .el-dialog__header {
  padding: 0; /* 隐藏弹窗头部 */
}
::v-deep .video-player-dialog .el-dialog__body {
  padding: 0; /* 让视频填满弹窗 */
}
::v-deep .video-player-dialog .el-dialog__headerbtn {
  top: -30px; /* 把右上角关闭按钮提上去，不遮挡视频 */
  font-size: 24px;
}
::v-deep .video-player-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #fff; /* 关闭按钮设为白色 */
}

.dialog-video-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
  border-radius: 4px;
  overflow: hidden;
}

.preview-video-element {
  width: 100%;
  max-height: 80vh; /* 限制视频最大高度不要超过屏幕的 80% */
  display: block;
  object-fit: contain; /* 保持视频原本的宽高比 */
}

/* 聊天会话精细化时间节点样式 */
.chat-time-node {
  text-align: center;
  margin: 14px 0 10px 0;
  user-select: none;
}

.chat-time-node span {
  display: inline-block;
  background-color: rgba(0, 0, 0, 0.05); /* 淡淡的灰色胶囊背景 */
  color: #b0b0b0;                        /* 柔和的文字颜色 */
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.3px;
}

/* 已过期文件气泡的特殊样式（整体略微变灰，暗示不可点击） */
.msg-file.is-expired {
  background-color: #fafafa;
  border-color: #e8e8e8;
  cursor: not-allowed; /* 鼠标悬停时显示禁用图标 */
}
.msg-file.is-expired:active {
  background-color: #fafafa; /* 取消点击变暗的反馈 */
}

/* 红字和感叹号图标样式 */
.file-expired-tips {
  font-size: 12px;
  color: #fa5151; /* 微信经典报错红 */
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-weight: 500;
}

/* 让感叹号图标稍微有点间距 */
.file-expired-tips i {
  margin-right: 4px;
  font-size: 13px;
}

/* 当文件过期时，让右侧的文件图标半透明，进一步强化过期视觉 */
.is-expired .file-icon-box {
  opacity: 0.4;
}

/* ==================== 转账消息专属样式 ==================== */
.msg-transfer {
  width: 230px;
  background-color: #fa9d3b; /* 微信转账标准橙 */
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.transfer-main {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  color: #fff;
}
.transfer-icon-box {
  font-size: 32px;
  margin-right: 14px;
  background: rgba(255, 255, 255, 0.2);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.transfer-icon-box i {
  transform: rotate(45deg); /* 让刷新图标斜过来更像转账箭头 */
}
.transfer-amount {
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
}
.transfer-status {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 4px;
}
.transfer-footer {
  background-color: #fff;
  padding: 6px 16px;
  font-size: 11px;
  color: #999;
  border-top: 1px solid rgba(0,0,0,0.03);
}

/* ==================== 红包消息专属样式 ==================== */
.msg-lucky-money {
  width: 230px;
  background-color: #fa5151; /* 微信红包标志红 */
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  transition: opacity 0.2s;
}
.hb-main {
  display: flex;
  align-items: center;
  padding: 16px;
  color: #fff;
}
.hb-icon-box {
  font-size: 34px;
  color: #fce3a1; /* 微信红包的金黄色盖章色 */
  margin-right: 14px;
  display: flex;
  align-items: center;
}
.hb-info {
  flex: 1;
  overflow: hidden;
}
.hb-title {
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hb-status {
  font-size: 11px;
  color: #fce3a1;
  margin-top: 3px;
}
.hb-footer {
  background-color: #fff;
  padding: 6px 16px;
  font-size: 11px;
  color: #999;
  border-top: 1px solid rgba(0,0,0,0.03);
}

/* 微信特色：已被领完的红包，整体卡片颜色会淡化变灰变粉 */
.msg-lucky-money.is-opened {
  background-color: #fca1a1; /* 浅淡红 */
}
.msg-lucky-money.is-opened .hb-icon-box {
  color: rgba(255,255,255,0.6); /* 金色变白透明 */
}

/* 转账基础气泡（待接收） */
.msg-transfer {
  width: 230px;
  background-color: #fa9d3b;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  /* 【新增核心动画线】：让所有属性变化时都走0.3秒的丝滑渐变 */
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* 【已接收状态】的灰色/淡橙色过渡样式 */
.msg-transfer.is-received {
  background-color: #fcdbb3; /* 模拟微信收钱后，卡片颜色变浅半透明 */
}

/* 微信特色：收钱后左侧圆圈背景和里面的对勾也会跟着变淡 */
.msg-transfer.is-received .transfer-icon-box {
  background: rgba(255, 255, 255, 0.4);
  transform: rotate(0deg); /* 恢复正向显示对勾 */
}

/* 图标本身的旋转过渡动画 */
.transfer-icon-box i {
  transition: transform 0.3s ease;
}
.msg-transfer:not(.is-received) .transfer-icon-box i {
  transform: rotate(45deg); /* 待收钱时箭头倾斜 */
}

/* ==================== 音视频通话气泡基础样式 ==================== */
.msg-call-bubble {
  display: inline-flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 14px;
  color: #191919;
  max-width: 240px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  word-break: break-all;
}

/* 自己发出的气泡（居右，文字靠右，通常背景已经是微信绿，通话气泡在微信里依然是白色/或跟常规气泡一致） */
/* 注意：如果你的聊天界面整体“自己发的气泡”有统一绿色背景，通话消息可以继承或单独写 */
.msg-call-bubble.is-self {
  background-color: #95ec69; /* 微信标志绿 */
}

/* 对方发出的气泡（居左，白色） */
.msg-call-bubble.is-other {
  background-color: #ffffff;
}

/* 图标样式微调 */
.call-icon {
  font-size: 18px;
}
.msg-call-bubble.is-self .call-icon {
  margin-left: 10px; /* 自己发出的，图标在文字右侧，加左边距 */
}
.msg-call-bubble.is-other .call-icon {
  margin-right: 10px; /* 对方发出的，图标在文字左侧，加右边距 */
}

/* 核心内容区 */
.call-content {
  display: flex;
  align-items: center;
}

.call-text {
  line-height: 1.4;
}

/* 时长数字单独空开一点间距 */
.call-duration {
  margin-left: 6px;
  font-weight: 500;
}
</style>
